import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import { BoxSubscriptionStatus } from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { word } from "@modules/core/utils/i18n"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import dayjs from "dayjs"
import { Pause, Percent, Play, SquareMinus } from "lucide-react"

type MemberOfferItemSubscriptionMenuProps = {
  subscriptionDetails: BoxSubscriptionDetailsEntity
  setSubscriptionSelected: React.Dispatch<React.SetStateAction<BoxSubscriptionDetailsEntity | null>>
  setIsOfferSupensionVisible: React.Dispatch<React.SetStateAction<boolean>>
  setIsOfferDiscountVisible: React.Dispatch<React.SetStateAction<boolean>>
  handleDeleteSubscription: () => void
  className?: string
} & React.ComponentProps<"button">

export function MemberOfferItemSubscriptionMenu({
  subscriptionDetails,
  setSubscriptionSelected,
  setIsOfferSupensionVisible,
  setIsOfferDiscountVisible,
  handleDeleteSubscription,
  className,
  ...props
}: MemberOfferItemSubscriptionMenuProps) {
  const canEditMemberOffer = useBoxStaffPermission(BoxStaffPermissions.EDIT_MEMBER_OFFER)

  if (subscriptionDetails.status === BoxSubscriptionStatus.INACTIVE || !canEditMemberOffer) return <> </>

  const isInPause =
    subscriptionDetails.pause?.pausedAt &&
    ((subscriptionDetails.pause.resumeAt && dayjs(subscriptionDetails.pause.resumeAt).isAfter(dayjs())) ||
      !subscriptionDetails.pause.resumeAt)
      ? true
      : false

  const canApplyDiscountOrResumeOffer =
    subscriptionDetails.offer.type === BoxOfferType.Subscription &&
    (subscriptionDetails.status === BoxSubscriptionStatus.ACTIVE ||
      subscriptionDetails.status === BoxSubscriptionStatus.PAUSE ||
      subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT)

  const canRemoveSubscription =
    subscriptionDetails.offer.type === BoxOfferType.Subscription ||
    subscriptionDetails.offer.type === BoxOfferType.Millenium

  return (
    <EllipsisMenu propsContent={{ side: "bottom" }} className={className} {...props}>
      {subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT && (
        <DropdownMenuItem key="unavailableBecauseOfSEPA">
          <span className="ml-4 flex items-center py-1 text-white">
            {word("box.member.table.offer.item.menu.unavailableBecauseOfSEPA")}
          </span>{" "}
        </DropdownMenuItem>
      )}
      {canApplyDiscountOrResumeOffer && (
        <DropdownMenuItem
          key="resumeOffer"
          onClick={() => {
            setSubscriptionSelected(subscriptionDetails)
            setIsOfferSupensionVisible(true)
          }}
          disabled={subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT}
        >
          {isInPause ? (
            <>
              <Play color="currentColor" strokeWidth={1} size={16} />
              {word("box.member.table.offer.item.menu.resumeOffer")}
            </>
          ) : (
            <>
              <Pause color="currentColor" strokeWidth={1} size={16} />
              {word("box.member.table.offer.item.menu.suspendOffer")}
            </>
          )}
        </DropdownMenuItem>
      )}
      {canApplyDiscountOrResumeOffer && (
        <DropdownMenuItem
          key="applyDiscount"
          onClick={() => {
            setSubscriptionSelected(subscriptionDetails)
            setIsOfferDiscountVisible(true)
          }}
          disabled={subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT}
        >
          <Percent color="currentColor" strokeWidth={1} size={16} />
          {subscriptionDetails.coupon
            ? word("box.member.table.offer.item.menu.editDiscount")
            : word("box.member.table.offer.item.menu.applyDiscount")}
        </DropdownMenuItem>
      )}
      {canRemoveSubscription && (
        <DropdownMenuItem
          key="removeSubscription"
          variant="destructive"
          onClick={() => {
            setSubscriptionSelected(subscriptionDetails)
            handleDeleteSubscription()
          }}
        >
          <SquareMinus color="currentColor" strokeWidth={1} size={16} />
          {word("box.member.table.offer.item.menu.removeSubscription")}
        </DropdownMenuItem>
      )}
    </EllipsisMenu>
  )
}
